/**
 * editableLabel uses grainjs's input widget and adds UI and behavioral extensions:
 *   - Label width grows/shrinks with content (using a hidden sizer element)
 *   - On Escape, cancel editing and revert to original value
 *   - Clicking away or hitting Enter on empty value cancels editing too
 *
 * The structure is a wrapper diver with an input child: div > input. Supports passing in
 * DomElementArgs, which get passed to the underlying <input> element.
 *
 * TODO: Consider merging this into grainjs's input widget.
 */
import { dom, DomElementArg, styled } from 'grainjs';
import { Observable } from 'grainjs';

export const cssWrapper = styled('div', `
  position: relative;
  display: inline-block;
`);

export const cssLabelText = styled('div', `
  /* Reset apperance */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: inherit;
  margin: 0;
  border: none;
  outline: none;

  /* Size is determined by the hidden sizer, so take up 100% of width */
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  line-height: inherit;
  font-size: inherit;
  font-family: inherit;
  font-weight: inherit;
  background-color: inherit;
  color: inherit;
`);

export const cssLabelStatus = styled('div', `
  &-normal {}
  &-editing {}
  &-saving {}
`);

export const cssTextInput = styled('input', `
  outline: none;
  height: 28px;
  border: 1px solid grey;
  border-radius: 3px;
  padding: 0 6px;
`);

const cssSizer = styled('div', `
  visibility: hidden;
  overflow: visible;
  white-space: pre;

  &:empty:before {
    content: ' ';  /* Don't collapse */
  }
`);

type SaveFunc = (value: string) => Promise<void>;
type Status = 'normal' | 'editing' | 'saving';


/**
 * Provides a label that takes in an observable that is set on Enter or loss of focus. Escape
 * cancels editing. Label grows in size with typed input. Validation logic (if any) should happen in
 * the save function, to reject a value simply throw an error, this will revert to the saved one .
 */
export function editableLabel(label: Observable<string>, save: SaveFunc, ...args: DomElementArg[]) {
  let inputEl: HTMLInputElement;
  let sizer: HTMLSpanElement;
  let status: Observable<Status>

  function updateSizer() {
    sizer.textContent = inputEl.value;
  }

  ({inputEl, status} = rawTextInput(label, save, updateSizer, dom.cls(cssLabelText.className),
    dom.on('focus', () => inputEl.select()),
    ...args
  ));
  return cssWrapper(
    sizer = cssSizer(label.get()),
    inputEl,
    cssLabelStatus.cls((use) => '-' + use(status)),
  );
}

/**
 * Provides a text input element that pretty much behaves like the editableLabel only it shows as a
 * regular input within a rigid static frame. It takes in an observable that is setf on Enter or loss
 * of focus. Escape cancels editing. Validation logic (if any) should happen in the save function,
 * to reject a value simply throw an error, this will revert to the the saved one.
 */
export function textInput(label: Observable<string>, save: SaveFunc, ...args: DomElementArg[]) {
  const onChange = () => undefined;
  return rawTextInput(label, save, onChange, dom.cls(cssTextInput.className), ...args).inputEl;
}

/**
 * A helper that implements all the saving logic for both editableLabel and textInput.
 */
function rawTextInput(value: Observable<string>, save: SaveFunc, onChange: () => void,
                             ...args: DomElementArg[]) {
  const status = Observable.create<Status>(null, 'normal');
  let inputEl: HTMLInputElement;

  // When label changes updates the input, unless in the middle of editing.
  const lis = value.addListener((val) => { if (status.get() !== 'editing') { setValue(val); }});

  function setValue(val: string) {
    inputEl.value = val;
    onChange();
  }

  function revertToSaved() {
    setValue(value.get());
    status.set('normal');
    inputEl.blur();
  }

  async function saveEdit() {
    if (status.get() === 'editing') {
      status.set('saving');
      inputEl.disabled = true;
      // Ignore errors; save() callback is expected to handle their reporting.
      try { await save(inputEl.value); } catch (e) { /* ignore */ }
      inputEl.disabled = false;
      revertToSaved();
    } else if (status.get() === 'normal') {
      // If we are not editing, nothing to save, but lets end in the expected blurred state.
      inputEl.blur();
    }
  }

  inputEl = dom('input',
    dom.autoDispose(lis),
    dom.autoDispose(status),
    {type: 'text'},
    cssLabelStatus.cls((use) => '-' + use(status)),
    dom.on('input', () => { status.set('editing'); onChange(); }),
    dom.on('blur', saveEdit),
    // we set the attribute to the initial value and keep it updated for the convenience of usage
    // with selenium webdriver
    dom.attr('value', value),
    dom.onKeyDown({
      Escape: revertToSaved,
      Enter: saveEdit,
    }),
    ...args
  );
  return {inputEl, status};
}
